import { Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InstanceStatusBadge } from '@/components/StatusBadge';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  ProgressBar,
  Skeleton,
  type DataTableColumn,
} from '@/components/ui';
import { useLive } from '@/hooks/LiveProvider';
import { useT } from '@/i18n';
import { api, type Instance } from '@/lib/api';
import { formatBitrate, formatPlanBandwidth, formatRelative } from '@/lib/format';
import { paths } from '@/router/paths';

export function InstancesPage() {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const { snapshots } = useLive();
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    api.instances
      .list()
      .then(({ instances: list }) => setInstances(list))
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return instances
      .map((instance) => ({ ...instance, live: snapshots[instance.id] ?? instance.live ?? null }))
      .filter((instance) =>
        query
          ? [instance.name, instance.ifName, instance.plan.name, instance.ipv4Cidr]
              .join(' ')
              .toLowerCase()
              .includes(query)
          : true,
      );
  }, [instances, snapshots, search]);

  const columns: DataTableColumn<Instance>[] = [
    {
      key: 'name',
      header: t('instances.columnInstance'),
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-bold text-t0">{row.name}</p>
          <p className="truncate font-mono text-[11.5px] text-t2">
            {row.ifName} &middot; {row.ipv4Cidr}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <InstanceStatusBadge status={row.status} />
          {row.isDedicated && (
            <span className="rounded-full bg-acc-soft px-2 py-0.5 text-[10px] font-bold text-acc">
              {t('instances.dedicated')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'plan',
      header: t('instances.columnPlan'),
      hideBelow: 'md',
      render: (row) => (
        <div>
          <p className="text-t0">{row.plan.name}</p>
          <p className="text-[11.5px] text-t2">{formatPlanBandwidth(row.bandwidthLimitMbps)}</p>
        </div>
      ),
    },
    {
      key: 'peers',
      header: t('instances.columnPeers'),
      render: (row) => (
        <span className="tabular-nums">
          <span className="font-bold text-acc">{row.live?.onlinePeers ?? 0}</span>
          <span className="text-t1"> / {row.peerCount}</span>
          <span className="text-t2"> von {row.maxPeers}</span>
        </span>
      ),
    },
    {
      key: 'rate',
      header: t('instances.columnRate'),
      hideBelow: 'lg',
      render: (row) => (
        <div className="font-mono text-[11.5px] tabular-nums">
          <p className="text-t1">
            {formatBitrate(row.live?.rxBitsPerSecond ?? 0)} {t('peers.inSuffix')}
          </p>
          <p className="text-t2">
            {formatBitrate(row.live?.txBitsPerSecond ?? 0)} {t('peers.outSuffix')}
          </p>
        </div>
      ),
    },
    {
      key: 'utilization',
      header: t('instances.columnLoad'),
      hideBelow: 'lg',
      width: '150px',
      render: (row) => {
        const percent = row.live?.utilizationPercent ?? 0;
        return (
          <ProgressBar
            value={percent}
            height={6}
            color={percent > 90 ? 'var(--bad)' : percent > 70 ? 'var(--warn)' : 'var(--acc)'}
          />
        );
      },
    },
    {
      key: 'sync',
      header: t('instances.columnSync'),
      hideBelow: 'lg',
      align: 'right',
      render: (row) => <span className="text-[11.5px] text-t2">{formatRelative(row.lastSyncAt)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('instances.title')}
        subtitle={t('instances.subtitle')}
        actions={
          <Button icon={<Plus size={15} />} onClick={() => navigate(paths.plans)}>
            {t('dashboard.orderPlan')}
          </Button>
        }
      />

      {error && (
        <div className="mb-5 rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-4 py-3 text-[13px] font-medium text-bad">
          {error}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-[320px]" />
      ) : instances.length === 0 ? (
        <EmptyState
          icon="🌐"
          title={t('instances.emptyTitle')}
          description={t('instances.emptyText')}
          action={<Button onClick={() => navigate(paths.plans)}>{t('dashboard.emptyAction')}</Button>}
        />
      ) : (
        <Card padding="none">
          <div className="border-b border-line p-4">
            <div className="relative max-w-sm">
              <Search size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-t2" />
              <Input
                className="pl-9"
                placeholder={t('instances.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label={t('instances.searchLabel')}
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={rows}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(paths.instances.detail(row.id))}
            emptyMessage={t('instances.noMatch')}
          />
        </Card>
      )}
    </div>
  );
}
